import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Swords, Loader2, MessageSquare, Calendar, Baby, Shield, ChevronRight, Users
} from "lucide-react";

const API = `${getApiUrl()}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function MultiplayerWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNudge, setShowNudge] = useState(false);

  useEffect(() => {
    axios.get(`${API}/multiplayer/dashboard-widget`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  // Availability nudge (smaller prompt) — shown when no schedule set + reminders on
  useEffect(() => {
    (async () => {
      try {
        const [av, prefs] = await Promise.all([
          axios.get(`${API}/multiplayer/availability`).catch(() => ({ data: {} })),
          axios.get(`${API}/settings/preferences`).catch(() => ({ data: {} })),
        ]);
        const hasSchedule = Array.isArray(av.data?.slots) && av.data.slots.length > 0;
        const remindersOn = prefs.data?.multiplayerAvailabilityReminders !== false;
        setShowNudge(!hasSchedule && remindersOn);
      } catch { /* ignore */ }
    })();
  }, []);

  if (loading) {
    return (
      <Card className="bg-[#0A0A0A] border-white/5" data-testid="mp-widget-loading">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-white/30" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const hasListings = data.listing_count > 0;

  return (
    <Card className="bg-[#0A0A0A] border-white/5 card-hover" data-testid="mp-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-base font-medium flex items-center gap-2">
            <Swords className="w-4 h-4 text-cyan-400" />
            Multiplayer
          </CardTitle>
          <Link to="/multiplayer" className="text-xs text-white/40 hover:text-white flex items-center gap-1 transition-colors" data-testid="mp-widget-open">
            Open <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Availability nudge (small) */}
        {showNudge && (
          <Link
            to="/multiplayer"
            data-testid="mp-widget-availability-nudge"
            className="flex items-center gap-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 p-2 hover:bg-cyan-500/15 transition-colors"
          >
            <Calendar className="w-4 h-4 text-cyan-400 shrink-0" />
            <span className="text-cyan-200 text-xs flex-1">Set your availability to start getting matches</span>
            <ChevronRight className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
          </Link>
        )}

        {/* Status banners */}
        {data.blocked && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 p-2 flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-400 shrink-0" />
            <span className="text-red-300 text-xs">You're blocked — pending admin review.</span>
          </div>
        )}
        {!data.blocked && data.show_mark_badge && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 flex items-center gap-2">
            <Shield className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="text-amber-300 text-xs">{data.mark_count} active moderation marks on your account.</span>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2">
          <Link
            to="/multiplayer"
            className="flex flex-col items-start p-2.5 rounded-md bg-white/[0.03] border border-white/5 hover:border-white/15 transition-colors"
            data-testid="mp-widget-stat-listings"
          >
            <span className="text-[10px] uppercase tracking-wider text-white/40">Listings</span>
            <span className="text-xl font-bold text-white mt-0.5">{data.listing_count}</span>
            <span className="text-[10px] text-white/30">{data.listing_count === 1 ? "game" : "games"}</span>
          </Link>
          <div className="flex flex-col items-start p-2.5 rounded-md bg-white/[0.03] border border-white/5">
            <span className="text-[10px] uppercase tracking-wider text-white/40 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Live Matches
            </span>
            <span className="text-xl font-bold text-emerald-300 mt-0.5">{data.schedule_matches_total}</span>
            <span className="text-[10px] text-white/30">overlapping schedules</span>
          </div>
          <div className="flex flex-col items-start p-2.5 rounded-md bg-white/[0.03] border border-white/5">
            <span className="text-[10px] uppercase tracking-wider text-white/40 flex items-center gap-1">
              <Baby className="w-3 h-3" /> Parental
            </span>
            <span className={`text-sm font-semibold mt-0.5 ${data.parental_mode_active ? "text-emerald-300" : "text-white/40"}`}>
              {data.parental_mode_active ? "Active now" : "Off"}
            </span>
            <span className="text-[10px] text-white/30">{data.availability_enabled ? "schedule on" : "schedule paused"}</span>
          </div>
        </div>

        {!hasListings ? (
          <div className="text-center py-3">
            <p className="text-white/40 text-sm mb-2">No multiplayer listings yet.</p>
            <Link to="/multiplayer">
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" data-testid="mp-widget-cta-add">
                Find players →
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Recent billboard */}
            {data.recent_billboard?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> Recent on your billboards
                </p>
                <div className="space-y-1">
                  {data.recent_billboard.slice(0, 3).map(m => (
                    <Link
                      key={m.id}
                      to="/multiplayer"
                      className="flex items-start gap-2 p-2 rounded bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
                      data-testid={`mp-widget-billboard-${m.id}`}
                    >
                      {m.box_art_url && <img src={m.box_art_url} alt="" className="w-6 h-8 object-cover rounded shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-white/70 text-xs font-medium truncate">{m.game_name || "Game"}</span>
                          <span className="text-white/30 text-[10px]">· {m.user_name} · {timeAgo(m.created_at)}</span>
                        </div>
                        <p className="text-white/50 text-[11px] truncate">{m.message}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Suggested matches */}
            {data.suggested_matches?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5 flex items-center gap-1">
                  <Users className="w-3 h-3" /> Suggested matches
                </p>
                <div className="space-y-1">
                  {data.suggested_matches.map(g => (
                    <Link
                      key={g.master_game_id}
                      to="/multiplayer"
                      className="flex items-center gap-2 p-2 rounded bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
                      data-testid={`mp-widget-suggest-${g.master_game_id}`}
                    >
                      {g.box_art_url && <img src={g.box_art_url} alt="" className="w-6 h-8 object-cover rounded shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-white/70 text-xs font-medium truncate">{g.game_name || "Game"}</div>
                        <div className="text-white/40 text-[10px]">{g.platform || ""}</div>
                      </div>
                      <span className="text-emerald-300 text-[10px] bg-emerald-500/10 px-1.5 py-0.5 rounded shrink-0">
                        {g.schedule_matches} avail
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
